import { getDolarValue, convertirAMonedaLocal } from "./exchangeRateService";
import { client } from "./supabase";

export const getPipelineCobro = async (pais, monedaLocal = "CLP") => {
  const resultado = [];

  const addDias = (fechaStr, dias) => {
    const d = new Date(`${fechaStr}T12:00:00`);
    d.setDate(d.getDate() + dias);
    return d.toISOString().split("T")[0];
  };

  const primerDiaMes = (mesAnio) => {
    const [mm, yyyy] = mesAnio.split("-");
    return `${yyyy}-${mm}-01`;
  };

  // Último día del mes (mes 1-indexed). new Date(ano, mes, 0) = día 0 del mes siguiente
  // = último día del mes actual. Cartelería se cobra dentro del mes de servicio.
  // Se construye el string a mano para evitar drift de zona horaria de toISOString().
  const ultimoDiaMes = (mes, ano) => {
    const m = parseInt(mes, 10);
    const a = parseInt(ano, 10);
    const dia = new Date(a, m, 0).getDate();
    return `${a}-${String(m).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
  };

  const hoy = new Date().toISOString().split("T")[0];

  const { data: salesData } = await client
    .from("sales")
    .select("id, cliente, importe, moneda_dolar, mes_factura, ano, tipo")
    .eq("pais", pais)
    .eq("estatus", "100%")
    .eq("eliminada", false)
    .is("facturanumero", null);

  for (const s of salesData || []) {
    if (!s.mes_factura) continue;
    let fechaEstimada;
    if (s.tipo === "p") {
      const [mm, yyyy] = s.mes_factura.split("-");
      const ultimoDia = new Date(parseInt(yyyy, 10), parseInt(mm, 10), 0);
      fechaEstimada = addDias(ultimoDia.toISOString().split("T")[0], 60);
    } else {
      const fechaBase = primerDiaMes(s.mes_factura);
      fechaEstimada = addDias(fechaBase, 60);
    }
    if (fechaEstimada <= hoy) continue;

    const importeOriginal = Number(s.importe || 0);
    const importeCLP = s.moneda_dolar
      ? Math.round(importeOriginal * (getDolarValue(fechaEstimada, pais) || 1))
      : importeOriginal;

    resultado.push({
      id: `pipeline-sales-${s.id}`,
      cliente: s.cliente || "—",
      importe: importeOriginal,
      importeCLP,
      moneda_dolar: s.moneda_dolar || false,
      fecha_estimada: fechaEstimada,
      tipo: "pipeline",
      origen: "sales",
    });
  }

  const { data: contratosData } = await client
    .from("sales_contratos")
    .select("id, cliente, meses, moneda_dolar")
    .eq("pais", pais)
    .eq("estatus", "100%")
    .eq("eliminada", false);

  for (const c of contratosData || []) {
    for (const mes of c.meses || []) {
      if (mes.facturanumero) continue;
      if (!mes.mes && !mes.facturafecha) continue;
      const fechaBase = mes.facturafecha || primerDiaMes(mes.mes);
      const fechaEstimada = addDias(fechaBase, 60);
      if (fechaEstimada <= hoy) continue;

      const importeOriginal = Number(mes.importe || 0);
      const importeCLP = c.moneda_dolar
        ? Math.round(importeOriginal * (getDolarValue(fechaEstimada, pais) || 1))
        : importeOriginal;

      resultado.push({
        id: `pipeline-contrato-${c.id}-${fechaBase}`,
        cliente: c.cliente || "—",
        importe: importeOriginal,
        importeCLP,
        moneda_dolar: c.moneda_dolar || false,
        fecha_estimada: fechaEstimada,
        tipo: "pipeline",
        origen: "contratos",
      });
    }
  }

  const { data: carteleriaData } = await client
    .from("negocios_carteleria")
    .select(
      "id, nombre_proyecto, referencia_id, tipo_vertical, modalidad, " +
        "fee_gestion_unitario, fee_contenidos_unitario, incluye_contenidos, " +
        "contenidos_fecha_inicio, contenidos_fecha_fin, " +
        "cuota_hardware_opex, moneda_gestion, moneda_contenidos, moneda_hardware, " +
        "fecha_inicio, plazo_contrato_meses, " +
        "cantidad_pantallas, meses_facturacion, estado, eliminada"
    )
    .eq("pais", pais)
    .eq("eliminada", false)
    // Deuda técnica: negocios_carteleria.estado tiene casing inconsistente en DB
    // ("activo" vs "Activo"). Las filas con schedule de facturación usan "Activo".
    // Se incluyen ambas variantes hasta normalizar el dato en DB y consumidores.
    .in("estado", ["activo", "Activo", "100%"]);

  // Resolución de nombre para la columna CONCEPTO. Los contratos recurrentes (vertical EO/TD)
  // tienen nombre_proyecto = null; el nombre real vive en edificios.edificio (EO) o
  // clientes.nombre (TD), referenciado por referencia_id (mismo patrón que useGestionCalcs.js).
  // Los one-off (vertical OT) sí traen nombre_proyecto. Se hace lookup por ID (regla 8).
  const refEdificios = new Set();
  const refClientes = new Set();
  for (const n of carteleriaData || []) {
    if (n.nombre_proyecto) continue;
    if (n.tipo_vertical === "EO" && n.referencia_id != null) refEdificios.add(n.referencia_id);
    else if (n.tipo_vertical === "TD" && n.referencia_id != null) refClientes.add(n.referencia_id);
  }

  const edificiosMap = {};
  if (refEdificios.size > 0) {
    const { data: edifData } = await client
      .from("edificios")
      .select("id, edificio")
      .in("id", [...refEdificios]);
    for (const e of edifData || []) edificiosMap[e.id] = e.edificio;
  }

  const clientesMap = {};
  if (refClientes.size > 0) {
    const { data: cliData } = await client
      .from("clientes")
      .select("id, nombre")
      .in("id", [...refClientes]);
    for (const c of cliData || []) clientesMap[c.id] = c.nombre;
  }

  const resolverNombreCarteleria = (n) => {
    if (n.nombre_proyecto) return n.nombre_proyecto;
    if (n.tipo_vertical === "EO" && edificiosMap[n.referencia_id]) return edificiosMap[n.referencia_id];
    if (n.tipo_vertical === "TD" && clientesMap[n.referencia_id]) return clientesMap[n.referencia_id];
    return `Cartelería ref ${n.referencia_id}`;
  };

  // Proyección de cartelería recurrente. A diferencia de sales/contratos (que leen cuotas
  // ya cargadas), los contratos de cartelería sólo guardan en meses_facturacion los meses
  // YA facturados; los futuros hay que generarlos. Se replica el patrón de useGestionCalcs.js:
  // horizonte = fecha_inicio + plazo_contrato_meses, y cada mes genera gestión (+ contenidos
  // si aplica) y opex (si el contrato es OPEX).
  //
  // CRÍTICO: gestión, contenidos y opex se convierten a moneda local POR SEPARADO con su
  // propia moneda (moneda_gestion / moneda_contenidos / moneda_hardware) antes de sumar —
  // nunca sumar montos de distinta moneda en crudo (bug histórico UF+CLP). Gestión y
  // contenidos viajan en la MISMA factura SII (ver useGestionCalcs.js), así que se emiten
  // como una sola entry; opex es factura separada.
  const hoyDate = new Date(`${hoy}T00:00:00`);
  const primerDiaMesActual = new Date(hoyDate.getFullYear(), hoyDate.getMonth(), 1);

  for (const n of carteleriaData || []) {
    if (!n.fecha_inicio) continue;
    const plazoMeses = parseInt(n.plazo_contrato_meses, 10) || 0;
    if (plazoMeses === 0) continue;

    const pantallas = parseInt(n.cantidad_pantallas, 10) || 1;
    const nombreCarteleria = resolverNombreCarteleria(n);
    const fechaInicio = new Date(`${n.fecha_inicio}T00:00:00`);
    const fechaFin = new Date(fechaInicio);
    fechaFin.setMonth(fechaInicio.getMonth() + plazoMeses);
    fechaFin.setDate(fechaFin.getDate() - 1);

    // Ventana propia de contenidos (puede diferir del plazo del contrato base).
    const incluyeContenidos = !!n.incluye_contenidos;
    const contInicio = n.contenidos_fecha_inicio ? new Date(`${n.contenidos_fecha_inicio}T00:00:00`) : null;
    const contFin = n.contenidos_fecha_fin ? new Date(`${n.contenidos_fecha_fin}T00:00:00`) : null;

    // OPEX = cuota de hardware mensual (leasing). TODO: confirmar el determinante de OPEX —
    // se asume modalidad === "OPEX" (verificado en data: sólo esos contratos tienen entries
    // tipo "opex" en meses_facturacion). Se exige además cuota > 0 como guarda defensiva.
    const cuotaOpex = parseFloat(n.cuota_hardware_opex) || 0;
    const esOpex = (n.modalidad || "").toUpperCase() === "OPEX" && cuotaOpex > 0;

    // Meses ya facturados (excluir). Contenidos viaja dentro de la factura de gestión, así
    // que un mes con gestión facturada también cubre contenidos.
    const mesesFact =
      typeof n.meses_facturacion === "string" ? JSON.parse(n.meses_facturacion || "[]") : n.meses_facturacion || [];
    const facturadosGestion = new Set();
    const facturadosOpex = new Set();
    for (const mf of mesesFact) {
      if (!mf.facturanumero) continue;
      const k = `${parseInt(mf.mes, 10)}-${parseInt(mf.ano, 10)}`;
      if (mf.tipo === "opex") facturadosOpex.add(k);
      else facturadosGestion.add(k);
    }

    // Iterar mes a mes desde el mes actual (o fecha_inicio si es futura) hasta fechaFin.
    const cursor =
      fechaInicio > primerDiaMesActual
        ? new Date(fechaInicio.getFullYear(), fechaInicio.getMonth(), 1)
        : new Date(primerDiaMesActual);
    const finCursor = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), 1);

    while (cursor <= finCursor) {
      const mesNum = cursor.getMonth() + 1; // 1-12
      const anoNum = cursor.getFullYear();
      cursor.setMonth(cursor.getMonth() + 1);

      // fecha_estimada = último día del mes proyectado (cartelería se cobra dentro del
      // mes de servicio: junio → 30/06, julio → 31/07, etc.).
      const fechaEstimada = ultimoDiaMes(mesNum, anoNum);
      if (fechaEstimada <= hoy) continue;

      const key = `${mesNum}-${anoNum}`;
      const slotDate = new Date(anoNum, mesNum - 1, 1);

      // ── Gestión (+ contenidos bundled en la misma factura) ──
      if (!facturadosGestion.has(key)) {
        const gestionBase = (parseFloat(n.fee_gestion_unitario) || 0) * pantallas;
        let importeLocal = convertirAMonedaLocal(gestionBase, n.moneda_gestion, pais, fechaEstimada);
        if (incluyeContenidos) {
          const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
          if (enRango) {
            const contBase = (parseFloat(n.fee_contenidos_unitario) || 0) * pantallas;
            importeLocal += convertirAMonedaLocal(contBase, n.moneda_contenidos, pais, fechaEstimada);
          }
        }
        if (importeLocal > 0) {
          resultado.push({
            id: `pipeline-carteleria-${n.id}-${mesNum}-${anoNum}-gestion`,
            cliente: nombreCarteleria,
            importe: Math.round(importeLocal),
            moneda_dolar: false,
            fecha_estimada: fechaEstimada,
            tipo: "pipeline",
            origen: "carteleria",
          });
        }
      }

      // ── OPEX (hardware mensual, factura separada) ──
      if (esOpex && !facturadosOpex.has(key)) {
        const opexLocal = convertirAMonedaLocal(cuotaOpex * pantallas, n.moneda_hardware, pais, fechaEstimada);
        if (opexLocal > 0) {
          resultado.push({
            id: `pipeline-carteleria-${n.id}-${mesNum}-${anoNum}-opex`,
            cliente: nombreCarteleria,
            importe: Math.round(opexLocal),
            moneda_dolar: false,
            fecha_estimada: fechaEstimada,
            tipo: "pipeline",
            origen: "carteleria",
          });
        }
      }
    }
  }

  resultado.sort((a, b) => a.fecha_estimada.localeCompare(b.fecha_estimada));

  return resultado;
};
